import React, { useState, useEffect } from 'react';
import { RealtimeDBService, getAllUsers, getAllCourses as getAllCoursesFS, updateUserProfile } from '../../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../../context/UnifiedAuthContext';
import { USER_ROLES } from '../../../config/userRoles';
import { Card, Button } from '../../../components/ui/BaseComponents';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { AlertBanner } from '../../../components/ui/CommonComponents';
import { Logger } from '../../../utils/logger';

const log = Logger.create('ParticipantsTab');

function ParticipantsTab() {
  const { userProfile, promoteToTeacher, demoteToStudent, deleteUser } = useUnifiedAuth();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Role change dialog state
  const [roleChangeDialogOpen, setRoleChangeDialogOpen] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);

  // Fetch users and courses
  useEffect(() => {
    fetchUsersAndCourses();
  }, []);

  const fetchUsersAndCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch users from Firestore
      const usersArray = await getAllUsers();
      
      if (!usersArray || usersArray.length === 0) {
        setUsers([]);
        setLoading(false);
        return;
      }

      // Fetch courses to count memberships
      const coursesArray = await getAllCoursesFS();
      const coursesData = {};
      coursesArray.forEach(course => {
        coursesData[course.id] = course;
      });
      setCourses(coursesData);

      // Transform users data
      const transformedUsers = usersArray.map(user => {
        // Count courses where user is member
        const courseCount = coursesArray.filter(course =>
          course.members && course.members[user.id]
        ).length;

        return {
          id: user.id,
          displayName: user.displayName || user.email?.split('@')[0] || 'Unbekannt',
          email: user.email || '',
          role: user.role || USER_ROLES.STUDENT,
          courseCount,
          createdAt: user.createdAt || Date.now()
        };
      });

      // Sort by registration date (newest first)
      transformedUsers.sort((a, b) => b.createdAt - a.createdAt);

      setUsers(transformedUsers);
    } catch (err) {
      log.error('Error fetching users:', err);
      setError('Fehler beim Laden der Nutzer');
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      user.displayName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower);
    
    const matchesRole = !roleFilter || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Change role
  const openRoleChangeDialog = (user, newRole) => {
    setRoleChangeTarget({ user, newRole });
    setRoleChangeDialogOpen(true);
  };

  const handleRoleChange = async () => {
    if (!roleChangeTarget) return;

    const { user, newRole } = roleChangeTarget;

    try {
      if (newRole === USER_ROLES.TEACHER) {
        await promoteToTeacher(user.id);
      } else if (newRole === USER_ROLES.STUDENT) {
        await demoteToStudent(user.id);
      } else if (newRole === USER_ROLES.ADMIN) {
        // Promote to admin - use Firestore (single source of truth)
        await updateUserProfile(user.id, {
          role: USER_ROLES.ADMIN,
          promotedAt: Date.now(),
          promotedBy: userProfile?.uid
        });
        // Note: No RTDB update - Firestore is the only source for user profiles
      }

      setSuccess(`Rolle geändert zu ${newRole}`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh users
      await fetchUsersAndCourses();
    } catch (err) {
      log.error('Error changing role:', err);
      setError(err.message || 'Fehler beim Ändern der Rolle');
    } finally {
      setRoleChangeTarget(null);
      setRoleChangeDialogOpen(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);

      // Also remove from all course memberships
      const coursesToUpdate = Object.entries(courses).filter(([_, course]) =>
        course.members && course.members[userToDelete.id]
      );

      for (const [courseId] of coursesToUpdate) {
        await RealtimeDBService.remove(`courses/${courseId}/members/${userToDelete.id}`);
      }

      setSuccess('Nutzer gelöscht');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh users
      await fetchUsersAndCourses();
    } catch (err) {
      log.error('Error deleting user:', err);
      setError(err.message || 'Fehler beim Löschen des Nutzers');
    } finally {
      setUserToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Check if user can be deleted
  const canDeleteUser = (user) => {
    // Cannot delete self
    if (user.id === userProfile?.uid) return false;
    // Cannot delete admins
    if (user.role === USER_ROLES.ADMIN) return false;
    // Must be admin to delete
    return userProfile?.role === USER_ROLES.ADMIN;
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  // Get role badge color
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
      case USER_ROLES.TEACHER:
        return 'bg-primary-500/10 text-primary-600 dark:text-primary-400 border-primary-500/20';
      case USER_ROLES.STUDENT:
        return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
      default:
        return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
    }
  };

  return (
    <Card>
      {/* Alert Messages */}
      {error && (
        <AlertBanner variant="danger" className="mb-4">
          {error}
        </AlertBanner>
      )}
      {success && (
        <AlertBanner variant="success" className="mb-4">
          {success}
        </AlertBanner>
      )}

      {/* Search and Filter */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <input
          type="text"
          placeholder="Nutzer durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary theme-transition"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary theme-transition"
        >
          <option value="">Alle Rollen</option>
          <option value={USER_ROLES.STUDENT}>STUDENT</option>
          <option value={USER_ROLES.TEACHER}>TEACHER</option>
          <option value={USER_ROLES.ADMIN}>ADMIN</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <p className="text-center py-8 text-muted-foreground">Lade Nutzer...</p>
      )}

      {/* Empty State */}
      {!loading && filteredUsers.length === 0 && !searchQuery && (
        <p className="text-center py-12 text-muted-foreground">
          Keine Nutzer gefunden
        </p>
      )}

      {/* Search Empty State */}
      {!loading && filteredUsers.length === 0 && searchQuery && (
        <p className="text-center py-8 text-muted-foreground">
          Keine Nutzer gefunden für "{searchQuery}"
        </p>
      )}

      {/* Users Table */}
      {!loading && filteredUsers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 font-medium text-foreground">Name</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">E-Mail</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Rolle</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Kurse</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Registriert</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr 
                  key={user.id}
                  className={`border-b border-border hover:bg-muted/50 theme-transition ${
                    user.id === userProfile?.uid ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="py-3 px-4">
                    <p className="font-medium text-foreground">
                      {user.displayName}
                      {user.id === userProfile?.uid && (
                        <span className="ml-2 text-xs text-primary">(Du)</span>
                      )}
                    </p>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground text-xs">
                    {user.email}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {user.courseCount}
                  </td>
                  <td className="py-3 px-4 text-center text-muted-foreground text-xs">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {/* Role Change Dropdown */}
                      {userProfile?.role === USER_ROLES.ADMIN && user.id !== userProfile?.uid && (
                        <select
                          value={user.role}
                          onChange={(e) => openRoleChangeDialog(user, e.target.value)}
                          className="px-2 py-1 border border-border rounded bg-background text-foreground text-xs"
                        >
                          <option value={USER_ROLES.STUDENT}>STUDENT</option>
                          <option value={USER_ROLES.TEACHER}>TEACHER</option>
                          <option value={USER_ROLES.ADMIN}>ADMIN</option>
                        </select>
                      )}

                      {/* Delete Button */}
                      {canDeleteUser(user) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openDeleteDialog(user)}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        title="Nutzer wirklich löschen?"
        message={`"${userToDelete?.displayName}" (${userToDelete?.email}) wird mit allen Daten gelöscht.\n\nDiese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        variant="danger"
      />

      {/* Role Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={roleChangeDialogOpen}
        onClose={() => {
          setRoleChangeDialogOpen(false);
          setRoleChangeTarget(null);
        }}
        onConfirm={handleRoleChange}
        title="Rolle ändern?"
        message={`Nutzer "${roleChangeTarget?.user?.displayName}" erhält die Rolle ${roleChangeTarget?.newRole}.${
          roleChangeTarget?.newRole === USER_ROLES.ADMIN 
            ? '\n\nWARNING: Admin-Rechte erlauben VOLLEN Zugriff auf alle Daten!' 
            : ''
        }`}
        confirmLabel="Rolle ändern"
        variant="warning"
      />
    </Card>
  );
}

export default ParticipantsTab;
